import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Trash2, Plus } from "lucide-react";

export const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

export function fmtErr(e) {
  const d = e?.response?.data?.detail ?? e?.message ?? "Terjadi kesalahan";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => (x && typeof x.msg === "string" ? x.msg : JSON.stringify(x))).join(", ");
  if (d && typeof d.msg === "string") return d.msg;
  return String(d);
}

export function TField({ label, value = "", onChange, type = "text", placeholder }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
        placeholder={placeholder}
        data-testid={`field-${label.toLowerCase().replace(/\s+/g, "-")}`}
      />
    </div>
  );
}

export function BField({ label, value = {}, onChange, textarea = false, rows = 3 }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-slate-600">
        {label} <span className="font-normal text-slate-400">(EN kiri · ID kanan)</span>
      </Label>
      <div className="grid gap-2 sm:grid-cols-2">
        {["en", "id"].map((l) => {
          const Tag = textarea ? Textarea : Input;
          return (
            <Tag
              key={l}
              value={value?.[l] || ""}
              onChange={(e) => onChange({ ...value, [l]: e.target.value })}
              placeholder={l.toUpperCase()}
              rows={textarea ? rows : undefined}
              data-testid={`field-${label.toLowerCase().replace(/\s+/g, "-")}-${l}`}
            />
          );
        })}
      </div>
    </div>
  );
}

export function ImageField({ label, value, onChange }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/admin/upload", fd);
      onChange(r.data.url);
      toast.success("Gambar terupload");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      <div className="flex items-start gap-3">
        {value ? (
          <img src={value} alt="" className="h-14 w-14 shrink-0 rounded-lg border border-slate-200 object-cover" />
        ) : (
          <div className="h-14 w-14 shrink-0 rounded-lg border border-dashed border-slate-300 bg-slate-50" />
        )}
        <div className="flex-1 space-y-2">
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="/images/… atau URL" data-testid={`field-${label.toLowerCase().replace(/\s+/g, "-")}-url`} />
          <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg" onChange={onFile} data-testid={`field-${label.toLowerCase().replace(/\s+/g, "-")}-upload`} className="block w-full text-xs text-slate-500 file:mr-2 file:rounded-full file:border-0 file:bg-electric-soft file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-electric hover:file:bg-blue-100" />
          {busy && <p className="text-xs text-slate-400">Mengunggah…</p>}
        </div>
      </div>
    </div>
  );
}

export function MetricsEditor({ metrics = [], onChange }) {
  const setVal = (i, v) => onChange(metrics.map((m, j) => (j === i ? { ...m, value: v } : m)));
  const setLabel = (i, lang, v) => onChange(metrics.map((m, j) => (j === i ? { ...m, label: { ...(m.label || {}), [lang]: v } } : m)));
  return (
    <div className="space-y-2 rounded-2xl bg-slate-50 p-4">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold text-slate-600">Metrik hasil</Label>
        <Button type="button" size="sm" variant="outline" onClick={() => onChange([...metrics, { value: "", label: { en: "", id: "" } }])} data-testid="add-metric-button">
          <Plus className="h-3.5 w-3.5" /> Metrik
        </Button>
      </div>
      {metrics.length === 0 && <p className="text-xs text-slate-400">Belum ada metrik. Klik “+ Metrik” untuk menambah.</p>}
      {metrics.map((m, i) => (
        <div key={i} className="grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
          <Input value={m.value || ""} onChange={(e) => setVal(i, e.target.value)} placeholder="Nilai (mis. 5x)" data-testid={`metric-value-${i}`} />
          <Input value={m.label?.en || ""} onChange={(e) => setLabel(i, "en", e.target.value)} placeholder="Label EN" data-testid={`metric-label-en-${i}`} />
          <Input value={m.label?.id || ""} onChange={(e) => setLabel(i, "id", e.target.value)} placeholder="Label ID" data-testid={`metric-label-id-${i}`} />
          <Button type="button" size="icon" variant="outline" onClick={() => onChange(metrics.filter((_, j) => j !== i))} data-testid={`remove-metric-${i}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
    </div>
  );
}

export function ChartEditor({ data = [], onChange }) {
  const setLabel = (i, lang, v) => onChange(data.map((d, j) => (j === i ? { ...d, label: { ...(d.label || {}), [lang]: v } } : d)));
  const setValue = (i, v) => onChange(data.map((d, j) => (j === i ? { ...d, value: v === "" ? "" : Number(v) } : d)));
  return (
    <div className="space-y-2 rounded-2xl bg-slate-50 p-4">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold text-slate-600">Data grafik pertumbuhan</Label>
        <Button type="button" size="sm" variant="outline" onClick={() => onChange([...data, { label: { en: "", id: "" }, value: 0 }])} data-testid="add-chart-point-button">
          <Plus className="h-3.5 w-3.5" /> Titik
        </Button>
      </div>
      {data.length === 0 && <p className="text-xs text-slate-400">Belum ada data grafik. Tambahkan titik (label + angka) — hanya data asli dari dashboard.</p>}
      {data.map((d, i) => (
        <div key={i} className="grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
          <Input value={d.label?.en || ""} onChange={(e) => setLabel(i, "en", e.target.value)} placeholder="Label EN" data-testid={`chart-label-en-${i}`} />
          <Input value={d.label?.id || ""} onChange={(e) => setLabel(i, "id", e.target.value)} placeholder="Label ID" data-testid={`chart-label-id-${i}`} />
          <Input type="number" value={d.value ?? ""} onChange={(e) => setValue(i, e.target.value)} placeholder="Angka" data-testid={`chart-value-${i}`} />
          <Button type="button" size="icon" variant="outline" onClick={() => onChange(data.filter((_, j) => j !== i))} data-testid={`remove-chart-point-${i}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
    </div>
  );
}

export function Field({ def, value, onChange }) {
  switch (def.type) {
    case "chart":
      return <ChartEditor data={value || []} onChange={onChange} />;
    case "bi":
      return <BField label={def.label} value={value || {}} onChange={onChange} />;
    case "bitextarea":
      return <BField label={def.label} value={value || {}} onChange={onChange} textarea rows={def.rows || 4} />;
    case "number":
      return <TField label={def.label} value={value} onChange={onChange} type="number" />;
    case "comma":
      return <TField label={def.label} value={Array.isArray(value) ? value.join(", ") : value || ""} onChange={(v) => onChange(v.split(/,\s*/).filter(Boolean))} />;
    case "image":
      return <ImageField label={def.label} value={value} onChange={onChange} />;
    case "metrics":
      return <MetricsEditor metrics={value || []} onChange={onChange} />;
    default:
      return <TField label={def.label} value={value} onChange={onChange} />;
  }
}

export function ItemShell({ title, onSave, onDelete, busy, children }) {
  return (
    <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="admin-item-card">
      <div className="flex items-center justify-between gap-3">
        <h4 className="truncate text-sm font-bold text-slate-800">{title || "(tanpa judul)"}</h4>
        <div className="flex shrink-0 gap-2">
          <Button size="sm" variant="outline" onClick={onDelete} data-testid="item-delete-button">
            <Trash2 className="h-4 w-4" />
          </Button>
          <Button size="sm" onClick={onSave} disabled={busy} data-testid="item-save-button">
            {busy ? "Menyimpan…" : "Save & Publish"}
          </Button>
        </div>
      </div>
      {children}
    </div>
  );
}

export function EditorItem({ collection, item, schema, titleKey, refresh }) {
  const [form, setForm] = useState(item);
  const [busy, setBusy] = useState(false);
  useEffect(() => setForm(item), [item]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/${collection}/${item.id}`, form);
      toast.success("Tersimpan & tayang di website");
      refresh();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Hapus item ini?")) return;
    try {
      await api.delete(`/admin/${collection}/${item.id}`);
      toast.success("Item dihapus");
      refresh();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const set = (key) => (v) => setForm((f) => ({ ...f, [key]: v }));
  const title = typeof titleKey === "function" ? titleKey(form) : form[titleKey];

  return (
    <ItemShell title={title} onSave={save} onDelete={del} busy={busy}>
      {schema.map((def) => (
        <Field key={def.key} def={def} value={form[def.key]} onChange={set(def.key)} />
      ))}
    </ItemShell>
  );
}

export function ListEditor({ collection, items = [], schema, blank, titleKey, refresh }) {
  const add = async () => {
    try {
      await api.post(`/admin/${collection}`, blank());
      toast.success("Item baru ditambahkan — isi lalu klik Save");
      refresh();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={add} data-testid={`add-${collection}-button`}>
          <Plus className="h-4 w-4" /> Tambah
        </Button>
      </div>
      {items.map((item) => (
        <EditorItem key={item.id} collection={collection} item={item} schema={schema} titleKey={titleKey} refresh={refresh} />
      ))}
    </div>
  );
}

export function SettingsForm({ settings = {}, schema, refresh, testid }) {
  const [form, setForm] = useState(settings);
  const [busy, setBusy] = useState(false);
  useEffect(() => setForm(settings), [settings]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Tersimpan & tayang di website");
      refresh();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const set = (key) => (v) => setForm((f) => ({ ...f, [key]: v }));

  return (
    <div className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid={testid}>
      {schema.map((def) => (
        <Field key={def.key} def={def} value={form[def.key]} onChange={set(def.key)} />
      ))}
      <Button onClick={save} disabled={busy} data-testid="settings-save-button">
        {busy ? "Menyimpan…" : "Save & Publish"}
      </Button>
    </div>
  );
}
